import { Controller } from '@hotwired/stimulus'

// Scrolls to and highlights the URL hash anchor element on page load,
// including elements inside lazy-loaded Turbo frames.
// Targets linked from a scrollspy TOC use native anchor scrolling instead.
//
// Supports an optional offset (default 50px) to account for sticky headers:
//   data-scroll-to-hash-offset-value="60"
export default class extends Controller {
    static values = { offset: { type: Number, default: 100 } }

    initialize() {
        this.scrollToHash = this.scrollToHash.bind(this)
        this.onHashChange = this.onHashChange.bind(this)
    }

    connect() {
        this.scrollToHash()
        this.element.addEventListener('turbo:frame-load', this.scrollToHash)
        window.addEventListener('hashchange', this.onHashChange)
    }

    disconnect() {
        this.element.removeEventListener('turbo:frame-load', this.scrollToHash)
        window.removeEventListener('hashchange', this.onHashChange)
    }

    onHashChange() {
        this.scrollToHash()
    }

    targetForHash(hash) {
        try {
            return hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null
        } catch {
            return null
        }
    }

    isTocTarget(target) {
        return [...document.querySelectorAll('[data-controller~="scrollspy"] a[href^="#"]')].some(
            (link) => this.targetForHash(link.hash) === target,
        )
    }

    scrollToHash() {
        setTimeout(() => {
            const hash = window.location.hash
            if (!hash) return

            const target = this.targetForHash(hash)
            if (!target || this.isTocTarget(target)) return

            const scrollToTarget = () => {
                const top =
                    target.getBoundingClientRect().top + window.pageYOffset - this.offsetValue
                window.scrollTo({ top })
            }

            // Scroll to the target with offset for top nav
            scrollToTarget()

            // Re-scroll if the page height shifts within 500ms (late Vue mounts)
            let lastHeight = document.documentElement.scrollHeight
            const deadline = performance.now() + 500
            const watch = () => {
                // An older anchor must not pull the page back after TOC navigation.
                if (
                    window.location.hash !== hash ||
                    !target.isConnected ||
                    this.isTocTarget(target)
                )
                    return
                const height = document.documentElement.scrollHeight
                if (height !== lastHeight) {
                    lastHeight = height
                    scrollToTarget()
                }
                if (performance.now() < deadline) {
                    requestAnimationFrame(watch)
                }
            }
            requestAnimationFrame(watch)

            const card = target.classList.contains('card')
                ? target
                : target.querySelector(':scope > .card') || target
            const cardHeader = card.querySelector('.card-header')
            if (cardHeader) {
                // Highlight the card header (if applicable)
                cardHeader.classList.add('animate__animated', 'animate__flash')
            } else {
                // Otherwise highlight the target element
                target.classList.add('animate__animated', 'animate__flash')
            }

            this.element.removeEventListener('turbo:frame-load', this.scrollToHash)
        }, 320) // Short timeout so Vue components don't shift scroll position
    }
}
